import * as XLSX from "xlsx";

/* ── Date formatter ── */
export const fmt = (d, opts = { day: "2-digit", month: "short", year: "numeric" }) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", opts); } catch { return "—"; }
};

/* ── API response array extractor ── */
export const extractArray = (res) => {
  if (!res) return [];
  for (const c of [
    res?.data?.data, res?.data?.rows, res?.data?.list,
    res?.data?.answers, res?.data?.history, res?.data, res,
  ])
    if (Array.isArray(c)) return c;
  return [];
};

/* ── API success check ── */
export const isOk = (res) =>
  Array.isArray(res) || Array.isArray(res?.data) ||
  res?.isSuccess === true || res?.success === true ||
  (typeof res?.status === "number" && res.status >= 200 && res.status < 300);

/* ── Avatar colour palette ── */
export const COLORS = [
  "#2563eb", "#16a34a", "#dc2626", "#d97706",
  "#7c3aed", "#0891b2", "#be185d", "#0f766e",
];
export const aColor = (id) => COLORS[Number(id || 0) % COLORS.length];

/* ── Supported units ── */
export const UNITS = ["kg", "ton", "litre", "kWh", "m³", "tCO₂e", "%", "hrs", "MWh", "GJ", "ML", "-"];

/* ── File-type helpers ── */
export const isSpreadsheet = (f) => /\.(csv|xlsx|xls)$/i.test(f?.name || "");

export const parseDate = (v) => {
  if (!v) return "";
  if (!isNaN(v) && Number(v) > 40000) {
    try {
      const d = XLSX.SSF.parse_date_code(Number(v));
      if (d) return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
    } catch { }
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10);
};

export const getCol = (row, ...keys) => {
  for (const k of keys) {
    const match = Object.keys(row).find(rk => rk.trim().toLowerCase() === k.toLowerCase());
    if (match !== undefined && row[match] !== "") return String(row[match]).trim();
  }
  return "";
};

export const getMimeLabel = (mime = "") => {
  if (mime.startsWith("image/")) return "Image";
  if (mime === "application/pdf") return "PDF";
  if (mime.includes("word")) return "Word Document";
  if (mime.includes("sheet") || mime.includes("excel")) return "Spreadsheet";
  return "Document";
};

export const isImage = (mime = "", url = "") =>
  mime.startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(url);

export const isPdf = (mime = "", url = "") =>
  mime === "application/pdf" || /\.pdf$/i.test(url);

/**
 * Start year of a financial year row ("2024-2025" → 2024). Entries with a
 * missing or unparsable `financial_year_value` yield `-Infinity` so they never
 * win the comparison and never throw — callers read this list straight from
 * localStorage, often inside a render-time useMemo.
 */
const fyStartYear = (fy) => {
  const start = parseInt(String(fy?.financial_year_value ?? "").split("-")[0], 10);
  return Number.isNaN(start) ? -Infinity : start;
};

/** Chronologically newest financial year, or null for an empty/missing list. */
export function getLatestFY(financialYears) {
  if (!financialYears?.length) return null;

  return financialYears.reduce((latest, current) =>
    fyStartYear(current) > fyStartYear(latest) ? current : latest
  );
}

export const formatKey = (key) => {
  if (!key) return "";

  const map = {
    module: "Module",
    subcategory: "Sub Category",
    kpi: "KPI",
    category: "Category",
    scope: "Scope",
  };

  const normalized = key.toLowerCase().replace(/\s+/g, "");

  if (map[normalized]) return map[normalized];

  // fallback: auto format (handles unknown keys)
  return key
    .replace(/([A-Z])/g, " $1")   // split camelCase
    .replace(/^./, (c) => c.toUpperCase()) // capitalize first
    .trim();
};

/**
 * Turns picker scope ids into the `locations` values the history endpoint
 * filters on, KEEPING the sub-location dimension: a sub-location scope is sent
 * as `sub_<id>`, a whole source as its bare id. The endpoint matches
 * `location_id IN (sources) OR sub_location_id IN (subs)`, so sending a
 * sub-location's parent id instead would match every sibling under that
 * parent — filtering to KMC-T1 would return KMC-T2 and the site's own records
 * as well.
 *
 * A dropped id would leave the request unscoped, which the backend answers with
 * every location, so ids that resolve to neither form are reported to the caller
 * rather than skipped.
 *
 * @param {Array<string|number>} scopeIds Ids taken from the location picker.
 * @param {Array<{id: string, locationId: number, subLocationId: (number|null)}>} locs
 *   Location scope options.
 * @returns {{locationParams: Array<string|number>, unresolved: Array<string|number>}}
 *   Unique scope values for the request, plus the ids that could not be resolved.
 */
export const resolveLocationParams = (scopeIds, locs) => {
  const locationParams = new Set();
  const unresolved = [];
  scopeIds.forEach((id) => {
    const match = locs.find((l) => String(l.id) === String(id));
    if (match) {
      locationParams.add(
        match.subLocationId != null
          ? `sub_${Number(match.subLocationId)}`
          : Number(match.locationId)
      );
      return;
    }
    const raw = String(id ?? "");
    if (raw.startsWith("sub_") && Number.isFinite(Number(raw.slice(4)))) {
      locationParams.add(raw);
      return;
    }
    if (raw !== "" && Number.isFinite(Number(raw))) {
      locationParams.add(Number(raw));
      return;
    }
    unresolved.push(id);
  });
  return { locationParams: [...locationParams], unresolved };
};
